<template>
  <div class="card dashboard-chart-card">
    <div class="card-header pb-0">
      <div class="d-flex align-items-center flex-wrap gap-2">
        <div class="me-auto">
          <h6 class="mb-0">Trabajos por operario</h6>
          <p class="text-sm text-secondary mb-0">{{ rangeLabel || 'Cantidad de trabajos registrados por usuario' }}</p>
        </div>
        <select
          v-model="selectedPeriod"
          class="form-select form-select-sm"
          style="width: auto; min-width: 130px;"
          @change="onPeriodChange"
        >
          <option value="day">Día</option>
          <option value="week">Semana</option>
          <option value="month">Mes</option>
        </select>
        <input
          v-if="selectedPeriod === 'day'"
          v-model="selectedDate"
          type="date"
          class="form-control form-control-sm"
          style="width: auto;"
          @change="loadChartData"
        />
      </div>
    </div>
    <div class="card-body p-3">
      <p v-if="isEmpty" class="text-sm text-muted mb-2">
        No hay trabajos registrados en este periodo.
      </p>
      <div class="chart-wrap" :style="{ height: chartHeight }">
        <canvas ref="chartCanvas"></canvas>
      </div>
    </div>
  </div>
</template>

<script>
import Chart from 'chart.js/auto';
import { colombiaTodayString } from '@/helpers/dateFormat';

export default {
  name: 'WorkerTasksByUserChart',
  data() {
    return {
      selectedPeriod: 'day',
      selectedDate: colombiaTodayString(),
      rangeLabel: '',
      isEmpty: false,
      labelsCount: 1,
      chart: null,
    };
  },
  computed: {
    chartHeight() {
      return `${Math.max(220, this.labelsCount * 42 + 60)}px`;
    },
  },
  methods: {
    normalizeArray(value) {
      if (Array.isArray(value)) {
        return value;
      }
      if (value && typeof value === 'object') {
        return Object.values(value);
      }
      return [];
    },
    destroyChart() {
      if (this.chart) {
        this.chart.destroy();
        this.chart = null;
      }
    },
    renderChart(labels, counts) {
      this.destroyChart();

      const normalizedLabels = this.normalizeArray(labels);
      const normalizedCounts = this.normalizeArray(counts).map((value) => Number(value) || 0);
      this.isEmpty = normalizedLabels.length === 0;

      const displayLabels = this.isEmpty ? ['Sin registros'] : normalizedLabels;
      const displayCounts = this.isEmpty ? [0] : normalizedCounts;
      this.labelsCount = displayLabels.length;

      this.$nextTick(() => {
        const canvas = this.$refs.chartCanvas;
        if (!canvas) {
          return;
        }

        const ctx = canvas.getContext('2d');
        this.chart = new Chart(ctx, {
          type: 'bar',
          data: {
            labels: displayLabels,
            datasets: [
              {
                label: 'Trabajos',
                data: displayCounts,
                backgroundColor: this.isEmpty
                  ? 'rgba(136, 152, 170, 0.45)'
                  : 'rgba(45, 206, 137, 0.85)',
                borderColor: this.isEmpty
                  ? 'rgba(136, 152, 170, 0.8)'
                  : 'rgba(45, 206, 137, 1)',
                borderWidth: 1,
                borderRadius: 4,
                maxBarThickness: 28,
              },
            ],
          },
          options: {
            indexAxis: 'y',
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              legend: {
                display: false,
              },
              tooltip: {
                enabled: !this.isEmpty,
                callbacks: {
                  label: (context) => `Trabajos: ${context.parsed.x ?? 0}`,
                },
              },
            },
            scales: {
              x: {
                beginAtZero: true,
                suggestedMax: this.isEmpty ? 1 : undefined,
                ticks: {
                  stepSize: 1,
                  precision: 0,
                },
                grid: {
                  drawBorder: false,
                },
              },
              y: {
                grid: {
                  display: false,
                },
              },
            },
          },
        });
      });
    },
    onPeriodChange() {
      if (this.selectedPeriod === 'day' && !this.selectedDate) {
        this.selectedDate = colombiaTodayString();
      }
      this.loadChartData();
    },
    async loadChartData() {
      try {
        const payload = {
          period: this.selectedPeriod,
          date: this.selectedPeriod === 'day' ? this.selectedDate : null,
        };
        const response = await this.$store.dispatch(
          'analitycs/getWorkerTasksByUser',
          payload
        );
        const data = response?.data ?? {};
        if (data.reference_date && this.selectedPeriod === 'day') {
          this.selectedDate = data.reference_date;
        }
        this.rangeLabel = data.range_label ?? '';
        this.renderChart(data.labels ?? [], data.counts ?? []);
      } catch (error) {
        console.error('Error loading worker tasks chart:', error);
        this.rangeLabel = '';
        this.renderChart([], []);
      }
    },
  },
  mounted() {
    this.loadChartData();
  },
  beforeUnmount() {
    this.destroyChart();
  },
};
</script>

<style scoped>
.chart-wrap {
  position: relative;
  min-height: 220px;
}
</style>
